import './InputGroupManager.scss';
import cn from 'classnames';
import { useEffect, useMemo, useState } from 'react';
import { Form, Modal, Card } from 'react-bootstrap';
import { MdAdd, MdDelete, MdEdit, MdKeyboardArrowUp, MdKeyboardArrowDown } from 'react-icons/md';
import { AlertDialog } from '../../Components/AlertDialog';
import { Button } from '../../Components/Button';
import { Dropdown } from '../../Components/Dropdown';
import { Appearance, Design, Intent, Placement, Size } from '../../Components/designConstants';
import { EmptyState } from '../../Components/EmptyState';
import { IconButton } from '../../Components/IconButton';
import { InteractiveDiv } from '../../Components/InteractiveEl';
import { Spinner } from '../../Components/Spinner';
import { useSetError } from '../../redux/modals';
import { useSlideSignedUrl } from '../SlideGenerator/useSlideSignedUrl';
import {
  InputDefinition,
  InputGroup,
  Presentation,
  saveInputGroupings,
  Slide,
  UNGROUPED_LABEL,
  useTemplateInputGroupings
} from './use-input-groupings';
import {
  addGroup,
  addInputToGroup,
  countSlideInputs,
  deleteGroup,
  getSlideConfigStatus,
  moveGroupDown,
  moveGroupUp,
  moveInputDown,
  moveInputUp,
  removeInputFromGroup,
  replaceGroup,
  SlideConfigStatus
} from './input-group-manager-utils';

interface GroupHandlers {
  moveGroupUp: (groupId: string) => void;
  moveGroupDown: (groupId: string) => void;
  editGroup: (group: InputGroup) => void;
  deleteGroup: (groupId: string) => void;
  moveInputUp: (groupId: string, inputName: string) => void;
  moveInputDown: (groupId: string, inputName: string) => void;
  removeInput: (groupId: string, inputName: string) => void;
  addInput: (groupId: string, inputName: string) => void;
}

export const InputGroupManager = () => {
  const setError = useSetError();
  const { data: presentations, isLoading, error, refetch } = useTemplateInputGroupings();

  const { originalMap, presentationOptions } = useMemo(() => {
    const list = presentations ?? [];
    return {
      originalMap: Object.fromEntries(list.map(p => [p.templateId, p])),
      presentationOptions: list.map(p => ({ label: p.templateName, value: p.templateId }))
    };
  }, [presentations]);

  const [editableMap, setEditableMap] = useState<Record<string, Presentation>>({});
  const [selectedPresentationId, setSelectedPresentationId] = useState<string>();
  const [selectedSlideId, setSelectedSlideId] = useState<string>();
  const [editingGroup, setEditingGroup] = useState<InputGroup | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showSaveSuccess, setShowSaveSuccess] = useState(false);

  // Seed the editable working copy from fetched data without clobbering in-flight edits: keep
  // any template already being edited, only adopt templates not yet in the editable map. (A save
  // explicitly re-adopts the just-saved template below to reset its baseline.)
  useEffect(() => {
    if (presentations) {
      setEditableMap(prev =>
        Object.fromEntries(presentations.map(p => [p.templateId, prev[p.templateId] ?? p]))
      );
    }
  }, [presentations]);

  // Surface load errors through the shared error modal.
  useEffect(() => {
    if (error) {
      setError({
        title: "Couldn't load templates",
        message: `Failed to load input groupings: ${error.message}`,
        reportError: error
      });
    }
  }, [error, setError]);

  // Default to the first template once options are available.
  useEffect(() => {
    if (!selectedPresentationId && presentationOptions.length > 0) {
      setSelectedPresentationId(presentationOptions[0].value);
    }
  }, [presentationOptions, selectedPresentationId]);

  const originalPresentation = selectedPresentationId
    ? originalMap[selectedPresentationId]
    : undefined;
  const selectedPresentation = selectedPresentationId
    ? editableMap[selectedPresentationId]
    : undefined;

  // Auto-select the first slide when the presentation changes.
  useEffect(() => {
    if (originalPresentation && originalPresentation.slides.length > 0) {
      setSelectedSlideId(originalPresentation.slides[0].slideId);
    }
  }, [originalPresentation]);

  const hasUnsavedChanges = useMemo(() => {
    if (!originalPresentation || !selectedPresentation) {
      return false;
    }
    return JSON.stringify(originalPresentation) !== JSON.stringify(selectedPresentation);
  }, [originalPresentation, selectedPresentation]);

  // Warn before a full-page unload (refresh/close) drops unsaved edits.
  useEffect(() => {
    if (!hasUnsavedChanges) {
      return undefined;
    }
    const warnBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', warnBeforeUnload);
    return () => window.removeEventListener('beforeunload', warnBeforeUnload);
  }, [hasUnsavedChanges]);

  const currentSlide = useMemo(() => {
    if (!selectedPresentation || !selectedSlideId) {
      return null;
    }
    return selectedPresentation.slides.find(slide => slide.slideId === selectedSlideId) ?? null;
  }, [selectedPresentation, selectedSlideId]);

  const configuredSlideCount = useMemo(() => {
    if (!selectedPresentation) {
      return 0;
    }
    return selectedPresentation.slides.filter(
      slide => getSlideConfigStatus(slide) === SlideConfigStatus.FULLY_CONFIGURED
    ).length;
  }, [selectedPresentation]);

  const updateEditablePresentation = (updated: Presentation) => {
    if (!selectedPresentationId) {
      return;
    }
    setEditableMap(prev => ({ ...prev, [selectedPresentationId]: updated }));
  };

  const resetEdits = () => {
    if (!selectedPresentationId || !originalPresentation) {
      return;
    }
    setEditableMap(prev => ({ ...prev, [selectedPresentationId]: originalPresentation }));
  };

  // Apply a pure transform to the current slide of the editable presentation.
  const withCurrentSlide = (
    transform: (presentation: Presentation, slideId: string) => Presentation
  ) => {
    if (!selectedPresentation || !currentSlide) {
      return;
    }
    updateEditablePresentation(transform(selectedPresentation, currentSlide.slideId));
  };

  const handleAddGroup = () =>
    withCurrentSlide((presentation, slideId) =>
      addGroup(presentation, slideId, crypto.randomUUID())
    );

  const handleSaveGroup = (updatedGroup: InputGroup) => {
    if (!editingGroup) {
      return;
    }
    withCurrentSlide((presentation, slideId) =>
      replaceGroup(presentation, slideId, editingGroup.groupId, updatedGroup)
    );
    setShowEditModal(false);
    setEditingGroup(null);
  };

  const groupHandlers: GroupHandlers = {
    moveGroupUp: groupId =>
      withCurrentSlide((presentation, slideId) => moveGroupUp(presentation, slideId, groupId)),
    moveGroupDown: groupId =>
      withCurrentSlide((presentation, slideId) => moveGroupDown(presentation, slideId, groupId)),
    editGroup: group => {
      setEditingGroup({ ...group });
      setShowEditModal(true);
    },
    deleteGroup: groupId =>
      withCurrentSlide((presentation, slideId) => deleteGroup(presentation, slideId, groupId)),
    moveInputUp: (groupId, inputName) =>
      withCurrentSlide((presentation, slideId) =>
        moveInputUp(presentation, slideId, groupId, inputName)
      ),
    moveInputDown: (groupId, inputName) =>
      withCurrentSlide((presentation, slideId) =>
        moveInputDown(presentation, slideId, groupId, inputName)
      ),
    removeInput: (groupId, inputName) =>
      withCurrentSlide((presentation, slideId) =>
        removeInputFromGroup(presentation, slideId, groupId, inputName)
      ),
    addInput: (groupId, inputName) =>
      withCurrentSlide((presentation, slideId) =>
        addInputToGroup(presentation, slideId, groupId, inputName)
      )
  };

  const saveChanges = async () => {
    if (!selectedPresentation || !selectedPresentationId) {
      return;
    }
    // Capture the id so a mid-save template switch can't misdirect the baseline update below.
    const presentationId = selectedPresentationId;
    try {
      setSaving(true);
      await saveInputGroupings(selectedPresentation);
      // Adopt the server's canonical copy as the new baseline AND editable copy so the unsaved
      // indicator clears (the server assigns its own group ids/order, so the local edited copy
      // and the refetched copy are structurally different until we re-adopt).
      const { data } = await refetch();
      const fresh = data?.find(p => p.templateId === presentationId);
      if (fresh) {
        setEditableMap(prev => ({ ...prev, [presentationId]: fresh }));
      }
      setShowSaveSuccess(true);
    } catch (e) {
      setError({
        message: `Failed to save input group mappings. Error: ${e.message}`,
        reportError: e
      });
    } finally {
      setSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className='input-group-manager'>
        <Spinner />
      </div>
    );
  }

  const canSave = Boolean(selectedPresentation) && hasUnsavedChanges && !saving;

  return (
    <div className='input-group-manager'>
      <div className='input-group-manager__header'>
        <div className='input-group-manager__title'>
          Input Group Manager
          {hasUnsavedChanges && <span className='input-group-manager__unsaved-indicator'>●</span>}
        </div>
        <div className='input-group-manager__controls'>
          <Dropdown
            overlayPlacement={Placement.RIGHT}
            size={Size.LG}
            label='Template'
            options={presentationOptions}
            onChange={value => {
              setSelectedPresentationId(value);
              setSelectedSlideId(undefined);
            }}
            value={selectedPresentationId}
          />
        </div>
      </div>

      <InputGroupManagerBody
        selectedPresentation={selectedPresentation}
        currentSlide={currentSlide}
        selectedSlideId={selectedSlideId}
        onSelectSlide={setSelectedSlideId}
        configuredCount={configuredSlideCount}
        onAddGroup={handleAddGroup}
        handlers={groupHandlers}
      />

      <div className='input-group-manager__footer'>
        <Button
          size={Size.LG}
          appearance={Appearance.OUTLINED}
          design={Intent.WARNING}
          onClick={resetEdits}
          disabled={!canSave}
        >
          Reset Changes
        </Button>
        <Button size={Size.LG} loading={saving} onClick={saveChanges} disabled={!canSave}>
          Save Groups
        </Button>
      </div>

      <EditGroupModal
        show={showEditModal}
        group={editingGroup}
        onChangeLabel={label => setEditingGroup(editingGroup ? { ...editingGroup, label } : null)}
        onClose={() => setShowEditModal(false)}
        onSave={() => editingGroup && handleSaveGroup(editingGroup)}
      />

      <AlertDialog
        intent={Intent.SUCCESS}
        show={showSaveSuccess}
        onHide={() => setShowSaveSuccess(false)}
        title='Saved'
        body='Input groups saved successfully.'
        primaryAction={{ label: 'Close' }}
      />
    </div>
  );
};

interface InputGroupManagerBodyProps {
  selectedPresentation: Presentation | undefined;
  currentSlide: Slide | null;
  selectedSlideId: string | undefined;
  onSelectSlide: (slideId: string) => void;
  configuredCount: number;
  onAddGroup: () => void;
  handlers: GroupHandlers;
}

const InputGroupManagerBody = ({
  selectedPresentation,
  currentSlide,
  selectedSlideId,
  onSelectSlide,
  configuredCount,
  onAddGroup,
  handlers
}: InputGroupManagerBodyProps) => {
  if (!selectedPresentation) {
    return (
      <div className='input-group-manager__empty'>
        <EmptyState
          title='No template selected'
          subtitle='Choose a template above to configure its input groups.'
        />
      </div>
    );
  }

  if (selectedPresentation.slides.length === 0) {
    return (
      <div className='input-group-manager__empty'>
        <EmptyState title='No slides' subtitle='This template has no slides to configure.' />
      </div>
    );
  }

  return (
    <div className='input-group-manager__body'>
      <SlideThumbnailSidebar
        slides={selectedPresentation.slides}
        selectedSlideId={selectedSlideId}
        onSelectSlide={onSelectSlide}
        configuredCount={configuredCount}
      />
      <SlideDisplay slide={currentSlide} />
      <ConfigPanel slide={currentSlide} onAddGroup={onAddGroup} handlers={handlers} />
    </div>
  );
};

interface SlideThumbnailSidebarProps {
  slides: Slide[];
  selectedSlideId: string | undefined;
  onSelectSlide: (slideId: string) => void;
  configuredCount: number;
}

const SlideThumbnailSidebar = ({
  slides,
  selectedSlideId,
  onSelectSlide,
  configuredCount
}: SlideThumbnailSidebarProps) => (
  <div className='input-group-manager__sidebar'>
    <div className='input-group-manager__slide-count'>
      {configuredCount}/{slides.length} Configured
    </div>
    <div className='input-group-manager__thumbnails'>
      {slides.map(slide => (
        <SlideThumbnail
          key={slide.slideId}
          slide={slide}
          active={selectedSlideId === slide.slideId}
          onSelect={() => onSelectSlide(slide.slideId)}
        />
      ))}
    </div>
  </div>
);

interface SlideThumbnailProps {
  slide: Slide;
  active: boolean;
  onSelect: () => void;
}

const SlideThumbnail = ({ slide, active, onSelect }: SlideThumbnailProps) => (
  <InteractiveDiv className={cn('input-group-manager__thumbnail', { active })} onClick={onSelect}>
    <div className='input-group-manager__thumbnail-number'>{slide.slideNumber}</div>
    {slide.imageUrl ? (
      <ThumbnailImage imageUrl={slide.imageUrl} slideNumber={slide.slideNumber} />
    ) : (
      <ThumbnailPlaceholder />
    )}
    <div className='input-group-manager__thumbnail-status'>
      <div
        className={cn(
          'input-group-manager__configured-indicator',
          `input-group-manager__configured-indicator--${getSlideConfigStatus(slide)}`
        )}
      />
    </div>
  </InteractiveDiv>
);

const ThumbnailPlaceholder = () => (
  <div className='input-group-manager__thumbnail-placeholder'>
    <span>No Image</span>
  </div>
);

interface ThumbnailImageProps {
  imageUrl: string;
  slideNumber: number;
}

// Signs this slide's image on its own via the shared useSlideSignedUrl hook — same per-slide
// query + cache keys as the Slide Generator page, so each thumbnail resolves independently and
// reuses any already-signed URL from the shared cache instead of re-signing.
const ThumbnailImage = ({ imageUrl, slideNumber }: ThumbnailImageProps) => {
  const { data: signedUrl, isLoading, isError } = useSlideSignedUrl(imageUrl);

  if (isLoading) {
    return <div className='input-group-manager__thumbnail-skeleton' />;
  }

  // Fall back to the placeholder rather than pointing <img> at a raw (unsigned) S3 key that the
  // browser can't fetch, which would render a broken-image icon.
  if (isError || !signedUrl) {
    return <ThumbnailPlaceholder />;
  }

  return (
    <img
      src={signedUrl}
      alt={`Slide ${slideNumber}`}
      className='input-group-manager__thumbnail-image'
      loading='lazy'
      decoding='async'
    />
  );
};

interface SlideDisplayProps {
  slide: Slide | null;
}

const SlideDisplay = ({ slide }: SlideDisplayProps) => {
  if (!slide) {
    return (
      <div className='input-group-manager__slide-display'>
        <div className='input-group-manager__no-slide'>
          <p>Select a slide from the sidebar to begin organizing inputs</p>
        </div>
      </div>
    );
  }

  return (
    <div className='input-group-manager__slide-display'>
      <div className='input-group-manager__slide-header'>
        <h4>Slide {slide.slideNumber}</h4>
        <p>{countSlideInputs(slide)} inputs total</p>
      </div>
      <div className='input-group-manager__slide-image-container'>
        {slide.imageUrl ? (
          <SlideImage imageUrl={slide.imageUrl} slideNumber={slide.slideNumber} />
        ) : (
          <SlidePlaceholder />
        )}
      </div>
    </div>
  );
};

const SlidePlaceholder = () => (
  <div className='input-group-manager__slide-placeholder'>
    <h5>No slide image available</h5>
    <p>Add an imageUrl to the slide data to display the slide image</p>
  </div>
);

interface SlideImageProps {
  imageUrl: string;
  slideNumber: number;
}

// Large slide preview, signed through the same shared per-slide hook as the thumbnails — so a
// slide already signed for its thumbnail resolves here instantly from the shared cache.
const SlideImage = ({ imageUrl, slideNumber }: SlideImageProps) => {
  const { data: signedUrl, isError } = useSlideSignedUrl(imageUrl);

  if (isError || !signedUrl) {
    return <SlidePlaceholder />;
  }

  return (
    <img
      src={signedUrl}
      alt={`Slide ${slideNumber}`}
      className='input-group-manager__slide-image'
      loading='lazy'
      decoding='async'
    />
  );
};

interface ConfigPanelProps {
  slide: Slide | null;
  onAddGroup: () => void;
  handlers: GroupHandlers;
}

const ConfigPanel = ({ slide, onAddGroup, handlers }: ConfigPanelProps) => {
  if (!slide) {
    return (
      <div className='input-group-manager__config-panel'>
        <div className='input-group-manager__no-slide-config'>
          <p>Select a slide to configure input groups</p>
        </div>
      </div>
    );
  }

  const ungroupedInputs =
    slide.inputGroups.find(group => group.label === UNGROUPED_LABEL)?.inputs ?? [];
  const groups = slide.inputGroups.filter(group => group.label !== UNGROUPED_LABEL);
  const totalInputs = countSlideInputs(slide);

  return (
    <div className='input-group-manager__config-panel'>
      <div className='input-group-manager__config-header'>
        <h5>Configure Inputs</h5>
        {totalInputs > 0 && (
          <Button leadingElement={<MdAdd />} onClick={onAddGroup}>
            Add Group
          </Button>
        )}
      </div>

      {ungroupedInputs.length > 0 && (
        <div className='input-group-manager__ungrouped-section'>
          <h6>Ungrouped Inputs</h6>
          <div className='input-group-manager__ungrouped-inputs'>
            {ungroupedInputs.map(input => (
              <span key={input.inputName} className='input-group-manager__ungrouped-badge'>
                {input.inputName}
              </span>
            ))}
          </div>
        </div>
      )}

      <div className='input-group-manager__groups-section'>
        <ConfigGroupsBody
          groups={groups}
          totalInputs={totalInputs}
          ungroupedInputs={ungroupedInputs}
          handlers={handlers}
        />
      </div>
    </div>
  );
};

interface ConfigGroupsBodyProps {
  groups: InputGroup[];
  totalInputs: number;
  ungroupedInputs: InputDefinition[];
  handlers: GroupHandlers;
}

const ConfigGroupsBody = ({
  groups,
  totalInputs,
  ungroupedInputs,
  handlers
}: ConfigGroupsBodyProps) => {
  if (totalInputs === 0) {
    return (
      <div className='input-group-manager__no-inputs'>
        <p>This slide has no inputs to configure.</p>
      </div>
    );
  }

  if (groups.length === 0) {
    return (
      <div className='input-group-manager__no-groups'>
        <p>No groups created yet. Click &quot;Add Group&quot; to start organizing inputs.</p>
      </div>
    );
  }

  return (
    <div className='input-group-manager__groups'>
      {groups.map((group, index) => (
        <GroupCard
          key={group.groupId}
          group={group}
          index={index}
          totalGroups={groups.length}
          ungroupedInputs={ungroupedInputs}
          handlers={handlers}
        />
      ))}
    </div>
  );
};

interface GroupCardProps {
  group: InputGroup;
  index: number;
  totalGroups: number;
  ungroupedInputs: InputDefinition[];
  handlers: GroupHandlers;
}

const GroupCard = ({ group, index, totalGroups, ungroupedInputs, handlers }: GroupCardProps) => (
  <Card className='input-group-manager__group'>
    <Card.Header className='input-group-manager__group-header'>
      <div className='input-group-manager__group-order'>
        <IconButton
          appearance={Appearance.OUTLINED}
          design={Design.NEUTRAL}
          size={Size.SM}
          aria-label={`Move ${group.label} up`}
          onClick={() => handlers.moveGroupUp(group.groupId)}
          disabled={index === 0}
        >
          <MdKeyboardArrowUp />
        </IconButton>
        <IconButton
          appearance={Appearance.OUTLINED}
          design={Design.NEUTRAL}
          size={Size.SM}
          aria-label={`Move ${group.label} down`}
          onClick={() => handlers.moveGroupDown(group.groupId)}
          disabled={index === totalGroups - 1}
        >
          <MdKeyboardArrowDown />
        </IconButton>
      </div>
      <div className='input-group-manager__group-info'>
        <h6 className='input-group-manager__group-title'>{group.label}</h6>
        <span className='input-group-manager__group-count'>{group.inputs.length} inputs</span>
      </div>
      <div className='input-group-manager__group-actions'>
        <IconButton
          appearance={Appearance.OUTLINED}
          design={Design.NEUTRAL}
          size={Size.SM}
          aria-label={`Rename ${group.label}`}
          onClick={() => handlers.editGroup(group)}
        >
          <MdEdit />
        </IconButton>
        <IconButton
          appearance={Appearance.OUTLINED}
          design={Intent.ERROR}
          size={Size.SM}
          aria-label={`Delete ${group.label}`}
          onClick={() => handlers.deleteGroup(group.groupId)}
        >
          <MdDelete />
        </IconButton>
      </div>
    </Card.Header>

    <Card.Body className='input-group-manager__group-content'>
      <div className='input-group-manager__inputs'>
        {group.inputs.map((input, inputIndex) => (
          <GroupInputRow
            key={`${group.groupId}-${input.inputName}`}
            group={group}
            input={input}
            inputIndex={inputIndex}
            handlers={handlers}
          />
        ))}
      </div>

      <div className='input-group-manager__add-input'>
        {ungroupedInputs.length > 0 && (
          <Dropdown
            overlayPlacement={Placement.RIGHT}
            size={Size.LG}
            label='Add input'
            value={undefined}
            options={ungroupedInputs.map(input => ({
              label: input.inputName,
              value: input.inputName
            }))}
            onChange={value => handlers.addInput(group.groupId, value)}
          />
        )}
      </div>
    </Card.Body>
  </Card>
);

interface GroupInputRowProps {
  group: InputGroup;
  input: InputDefinition;
  inputIndex: number;
  handlers: GroupHandlers;
}

const GroupInputRow = ({ group, input, inputIndex, handlers }: GroupInputRowProps) => (
  <div className='input-group-manager__input-item'>
    <div className='input-group-manager__input-order'>
      <IconButton
        appearance={Appearance.OUTLINED}
        design={Design.NEUTRAL}
        size={Size.SM}
        aria-label={`Move ${input.inputName} up`}
        onClick={() => handlers.moveInputUp(group.groupId, input.inputName)}
        disabled={inputIndex === 0}
      >
        <MdKeyboardArrowUp />
      </IconButton>
      <IconButton
        appearance={Appearance.OUTLINED}
        design={Design.NEUTRAL}
        size={Size.SM}
        aria-label={`Move ${input.inputName} down`}
        onClick={() => handlers.moveInputDown(group.groupId, input.inputName)}
        disabled={inputIndex === group.inputs.length - 1}
      >
        <MdKeyboardArrowDown />
      </IconButton>
    </div>
    <span className='input-group-manager__input-badge'>{input.inputName}</span>
    <IconButton
      appearance={Appearance.OUTLINED}
      design={Intent.ERROR}
      size={Size.SM}
      aria-label={`Remove ${input.inputName} from ${group.label}`}
      onClick={() => handlers.removeInput(group.groupId, input.inputName)}
    >
      <MdDelete />
    </IconButton>
  </div>
);

interface EditGroupModalProps {
  show: boolean;
  group: InputGroup | null;
  onChangeLabel: (label: string) => void;
  onClose: () => void;
  onSave: () => void;
}

const EditGroupModal = ({ show, group, onChangeLabel, onClose, onSave }: EditGroupModalProps) => (
  <Modal show={show} onHide={onClose}>
    <Modal.Header closeButton>
      <Modal.Title>Edit Group</Modal.Title>
    </Modal.Header>
    <Modal.Body>
      <Form.Group>
        <Form.Label>Group Name</Form.Label>
        <Form.Control
          type='text'
          value={group?.label || ''}
          onChange={e => onChangeLabel(e.target.value)}
          placeholder='Enter group name...'
        />
      </Form.Group>
    </Modal.Body>
    <Modal.Footer>
      <Button appearance={Appearance.OUTLINED} design={Design.NEUTRAL} onClick={onClose}>
        Cancel
      </Button>
      <Button onClick={onSave} disabled={!group?.label?.trim()}>
        Save
      </Button>
    </Modal.Footer>
  </Modal>
);
